import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Send, Clock, User, ChevronDown, ChevronUp, Filter, Mail } from 'lucide-react';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getTickets();
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (ticketId) => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }
    setReplying(true);
    try {
      await adminApi.replyTicket(ticketId, { message: replyText });
      toast.success('Reply sent & email notification sent to user');
      setReplyText('');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  const handleStatusChange = async (ticketId, status) => {
    try {
      await adminApi.updateTicketStatus(ticketId, status);
      toast.success(`Ticket status updated to ${status}`);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredTickets = filter === 'all'
    ? tickets
    : tickets.filter(t => t.status === filter);

  const statusColors = {
    open: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    awaiting_response: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    answered: 'bg-green-500/20 text-green-400 border-green-500/30',
    in_progress: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    closed: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
  };

  const statusLabels = {
    open: 'Open',
    awaiting_response: 'Awaiting Response',
    answered: 'Answered',
    in_progress: 'In Progress',
    closed: 'Closed',
  };

  const statusCounts = {
    all: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    awaiting_response: tickets.filter(t => t.status === 'awaiting_response').length,
    answered: tickets.filter(t => t.status === 'answered').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    closed: tickets.filter(t => t.status === 'closed').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-tickets">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-orange-500/10">
            <MessageSquare className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h1 className="text-2xl text-white font-medium">Support Tickets</h1>
            <p className="text-sm text-zinc-500">Manage user support requests</p>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {Object.entries(statusCounts).map(([key, count]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`p-3 rounded-lg text-center transition-all border ${
              filter === key ? 'border-amber-500/50 bg-amber-500/10' : 'border-white/5 bg-black/30 hover:border-white/10'
            }`}
            data-testid={`filter-${key}`}
          >
            <p className="text-lg text-white font-bold">{count}</p>
            <p className="text-[10px] text-zinc-500 capitalize">{key === 'all' ? 'All' : statusLabels[key] || key}</p>
          </button>
        ))}
      </div>

      {/* Ticket List */}
      {filteredTickets.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <MessageSquare className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-lg text-white mb-2">No tickets found</h3>
          <p className="text-zinc-500">
            {filter === 'all' ? 'No support tickets have been submitted yet.' : `No ${statusLabels[filter] || filter} tickets.`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTickets.map((ticket, idx) => {
            const isExpanded = expandedTicket === ticket.id;
            return (
              <motion.div
                key={ticket.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                className="glass-card overflow-hidden"
              >
                {/* Ticket Header */}
                <button
                  onClick={() => setExpandedTicket(isExpanded ? null : ticket.id)}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-white/[0.02] transition-colors"
                  data-testid={`ticket-row-${ticket.id}`}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-orange-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white font-medium truncate">{ticket.subject}</p>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusColors[ticket.status] || statusColors.open}`}>
                          {statusLabels[ticket.status] || ticket.status}
                        </span>
                        {ticket.category && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] text-zinc-400 bg-zinc-800 capitalize">
                            {ticket.category}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-zinc-500">{ticket.user_name}</span>
                        <span className="text-xs text-zinc-600">|</span>
                        <span className="text-xs text-zinc-500">{ticket.user_email}</span>
                        <span className="text-xs text-zinc-600">|</span>
                        <span className="text-xs text-zinc-600 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(ticket.created_at).toLocaleDateString()}
                        </span>
                        {ticket.replies?.length > 0 && (
                          <span className="text-xs text-amber-400">{ticket.replies.length} replies</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-white/5">
                    {/* Original Message */}
                    <div className="p-4 bg-black/20">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-blue-400" />
                        <span className="text-sm text-white font-medium">{ticket.user_name}</span>
                        <span className="text-xs text-zinc-500">{new Date(ticket.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-zinc-300 whitespace-pre-wrap pl-6">{ticket.message}</p>
                    </div>

                    {/* Replies Thread */}
                    {ticket.replies?.map((reply) => (
                      <div
                        key={reply.id}
                        className={`p-4 border-t border-white/5 ${reply.is_admin ? 'bg-amber-500/[0.03]' : 'bg-black/10'}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          {reply.is_admin ? (
                            <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                              <span className="text-[10px] text-black font-bold">A</span>
                            </div>
                          ) : (
                            <User className="w-4 h-4 text-blue-400" />
                          )}
                          <span className={`text-sm font-medium ${reply.is_admin ? 'text-amber-400' : 'text-white'}`}>
                            {reply.author_name}
                          </span>
                          <span className="text-xs text-zinc-500">{new Date(reply.created_at).toLocaleString()}</span>
                          {reply.is_admin && (
                            <span className="flex items-center gap-1 text-[10px] text-green-400">
                              <Mail className="w-3 h-3" /> Email sent
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-zinc-300 whitespace-pre-wrap pl-6">{reply.message}</p>
                      </div>
                    ))}

                    {/* Reply Box + Status Controls */}
                    <div className="p-4 border-t border-white/5 space-y-3">
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-zinc-500">Change Status:</span>
                        <Select value={ticket.status} onValueChange={(val) => handleStatusChange(ticket.id, val)}>
                          <SelectTrigger className="w-[180px] bg-black/50 border-white/10 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-white/10">
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="answered">Answered</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex gap-2">
                        <textarea
                          value={expandedTicket === ticket.id ? replyText : ''}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Type your reply... (user will receive email notification)"
                          className="flex-1 bg-black/50 border border-white/10 rounded-lg p-3 text-sm text-white placeholder:text-zinc-600 resize-none min-h-[80px] focus:outline-none focus:border-amber-500/50"
                          data-testid={`reply-input-${ticket.id}`}
                        />
                      </div>
                      <Button
                        onClick={() => handleReply(ticket.id)}
                        disabled={replying || !replyText.trim()}
                        className="gold-btn"
                        data-testid={`send-reply-${ticket.id}`}
                      >
                        {replying ? (
                          <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send Reply & Notify User
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
